/*global swal*/
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import SweetAlert from 'react-swal';
import { Icon, Button } from '../../../../components';
import { loadProvidingVirtualPractices, addVirtualPractice, approveVirtualPractice, loadReceivingVirtualPractices, removeVirtualPractice } from './api';
import LinkVirtualPracticeModal from './LinkVirtualPracticeModal';

export default class Practices extends React.Component {
	static propTypes = {
		adminId: PropTypes.number.isRequired,
		showNotification: PropTypes.func.isRequired,
		onChange: PropTypes.func
	}

	static defaultProps = {
		onChange: () => {}
	}

	state = {
		providingPractices: [],
		receivingPractices: [],
		showSwal: false,
		showLinkModal: false,
		isLoading: false
	}

	componentDidMount() {
		this.setState({ isLoading: true });
		this.refresh();
	}

	refresh = async () => {
		const { adminId } = this.props;
		const vppResult = await loadProvidingVirtualPractices(adminId);
		if (vppResult && vppResult.data && vppResult.data.status) {
			this.setState({ providingPractices: vppResult.data.status });
		}
		const vrpResult = await loadReceivingVirtualPractices(adminId);
		if (vrpResult && vrpResult.data && vrpResult.data.status) {
			this.setState({ receivingPractices: vrpResult.data.status });
		}
		this.setState({ isLoading: false });
	}

	handleOpenLinkModal = ({ currentTarget }) => {
		const { provider_practice_virtual_id } = currentTarget.dataset;
		this.setState({ showLinkModal: true, provider_practice_virtual_id });
	}

	handleAddVirtualPractice = async (providerCode) => {
		const { adminId, showNotification } = this.props;
		const { provider_practice_virtual_id } = this.state;
		this.setState({ showLinkModal: false, provider_practice_virtual_id: null, isLoading: true });
		if (providerCode && !provider_practice_virtual_id) {
			const { data } = await addVirtualPractice(adminId, providerCode);
			if (data && data.status && data.status.practice_added) {
				showNotification({ message: 'Practice Link Added', status: 'success', timeout: 3000 });
			} else {
				showNotification({ message: 'Unable to add Practice Link', status: 'danger', timeout: 10000 });
			}
		} else if (provider_practice_virtual_id && !providerCode) {
			const { data } = await approveVirtualPractice(adminId, provider_practice_virtual_id);
			if (data && data.status && data.status.approved) {
				showNotification({ message: 'Practice Link Approved', status: 'success', timeout: 3000 });
			} else {
				showNotification({ message: 'Unable to approve Practice Link', status: 'danger', timeout: 10000 });
			}
		}
		this.props.onChange();
		this.refresh();
	}

	handleCloseLinkModal = () => {
		this.setState({ showLinkModal: false, linkType: null });
	}

	handleShowRemoveVirtualPracticeWarning = ({ currentTarget }) => {
		const { provider_practice_virtual_id } = currentTarget.dataset;
		this.setState({
			showSwal: true,
			provider_practice_virtual_id
		});
	}

	handleUnlinkVirtualPractice = async (isConfirm) => {
		if (isConfirm) {
			const { adminId, showNotification } = this.props;
			const { provider_practice_virtual_id } = this.state;
			const { data } = await removeVirtualPractice(adminId, provider_practice_virtual_id);
			if (data && data.status && data.status.removed) {
				this.setState({ isLoading: true });
				showNotification({ message: 'Practice Unlinked', status: 'success', timeout: 3000 });
			} else {
				showNotification({ message: 'Unable to unlink Practice', status: 'danger', timeout: 10000 });
			}
			this.props.onChange();
			this.refresh();
		}
		swal.close();
		this.setState({
			showSwal: false,
			provider_practice_virtual_id: null
		});
	}

	render() {
		const { providingPractices, receivingPractices, isLoading, showLinkModal, provider_practice_virtual_id, showSwal } = this.state;
		return (
			<React.Fragment>
				<LinkVirtualPracticeModal
					isOpen={showLinkModal}
					onCancel={this.handleCloseLinkModal}
					onAdd={this.handleAddVirtualPractice}
					isProvidingPractice={!provider_practice_virtual_id}
				/>
				<SweetAlert
					isOpen={showSwal}
					type="warning"
					title="Are you sure?"
					text="This will also remove any linked Providers"
					showCancelButton
					confirmButtonColor="#DD6B55"
					confirmButtonText="Yes"
					closeOnConfirm={false}
					closeOnCancel={false}
					callback={this.handleUnlinkVirtualPractice}
				/>
				<div className="row">
					<div className="col-md-6">
						<div className="panel panel-default">
							<div className="panel-heading">
								Providing Practices
								<div className="pull-right">
									<Button
										primary
										small
										title="Add"
										onClick={this.handleOpenLinkModal}
									/>
								</div>
							</div>
							<div className="table-responsive">
								<table className={cn('table', 'table-bordered', 'table-hover', { 'whirl': isLoading, 'traditional': isLoading })}>
									<thead>
										<tr>
											<th>Originating Practice</th>
											<th>Status</th>
										</tr>
									</thead>
									<tbody>
										{providingPractices.map(({ practice_name, provider_practice_virtual_id, providing_approved_ind, receiving_approved_ind }) => (
											<tr key={provider_practice_virtual_id}>
												<td>{practice_name}</td>
												<td>
													{(Boolean(providing_approved_ind) && !receiving_approved_ind) && (
														<span>
															<Icon name="fa-clock-o" className="text-info" /> Awaiting Approval
														</span>
													)}
													{(Boolean(providing_approved_ind) && Boolean(receiving_approved_ind)) && (
														<span>
															Linked
															<Button
																title="Unlink"
																className="ml"
																danger
																data-provider_practice_virtual_id={provider_practice_virtual_id}
																onClick={this.handleShowRemoveVirtualPracticeWarning}
															/>
														</span>
													)}
												</td>
											</tr>
										))}
										{providingPractices.length === 0 && (
											<tr>
												<td colSpan="2">0 records found</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
					<div className="col-md-6">
						<div className="panel panel-default">
							<div className="panel-heading">
								Receiving Practices
							</div>
							<div className="table-responsive">
								<table className={cn('table', 'table-bordered', 'table-hover', { 'whirl': isLoading, 'traditional': isLoading })}>
									<thead>
										<tr>
											<th>Originating Practice</th>
											<th>Status</th>
										</tr>
									</thead>
									<tbody>
										{receivingPractices.map(({ practice_name, provider_practice_virtual_id, providing_approved_ind, receiving_approved_ind }) => (
											<tr key={provider_practice_virtual_id}>
												<td>{practice_name}</td>
												<td>
													{(Boolean(providing_approved_ind) && !receiving_approved_ind) && (
														<Button
															title="Approve Link"
															primary
															data-provider_practice_virtual_id={provider_practice_virtual_id}
															onClick={this.handleOpenLinkModal}
														/>
													)}
													{(Boolean(providing_approved_ind) && Boolean(receiving_approved_ind)) && (
														<span>
															Linked
															<Button
																title="Unlink"
																className="ml"
																danger
																data-provider_practice_virtual_id={provider_practice_virtual_id}
																onClick={this.handleShowRemoveVirtualPracticeWarning}
															/>
														</span>
													)}
												</td>
											</tr>
										))}
										{receivingPractices.length === 0 && (
											<tr>
												<td colSpan="2">0 records found</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</React.Fragment>
		);
	}
}
